import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { labelsApi, type Label } from '../labels/labelsApi';
import { DeleteButton } from '../components/DeleteButton';
import { PencilIcon } from '../components/icons';

const STARTER_LABELS: Array<{ name: string; color: string }> = [
  { name: 'Bugs', color: '#E53935' },
  { name: 'Security', color: '#FDD835' },
  { name: 'Missing', color: '#FB8C00' },
  { name: 'Feature', color: '#43A047' },
  { name: 'Optimization', color: '#1E88E5' },
  { name: 'Infrastructure', color: '#8E24AA' },
];

export default function LabelsPage() {
  const { workspaceId = '' } = useParams<{ workspaceId: string }>();
  const { getToken, refresh } = useAuth();

  const [labels, setLabels] = useState<Label[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#1E88E5');
  const [creating, setCreating] = useState(false);
  const [creatingStarterSet, setCreatingStarterSet] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('#1E88E5');
  const [saving, setSaving] = useState(false);

  const loadLabels = useCallback(() => {
    setLoading(true);
    labelsApi
      .list(workspaceId, getToken, refresh)
      .then(data => setLabels(data.labels))
      .catch(() => setError('Failed to load labels'))
      .finally(() => setLoading(false));
  }, [workspaceId, getToken, refresh]);

  useEffect(() => {
    loadLabels();
  }, [loadLabels]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    setError('');
    try {
      await labelsApi.create(workspaceId, newName.trim(), newColor, getToken, refresh);
      setNewName('');
      setShowCreateForm(false);
      loadLabels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create label');
    } finally {
      setCreating(false);
    }
  }

  async function handleCreateStarterSet() {
    setCreatingStarterSet(true);
    setError('');
    try {
      const existingNames = new Set(labels.map(label => label.name));
      for (const starter of STARTER_LABELS) {
        if (existingNames.has(starter.name)) continue;
        await labelsApi.create(workspaceId, starter.name, starter.color, getToken, refresh);
      }
      loadLabels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create starter labels');
    } finally {
      setCreatingStarterSet(false);
    }
  }

  function startEdit(label: Label) {
    setEditingId(label.labelId);
    setEditName(label.name);
    setEditColor(label.color);
  }

  async function handleSave(e: FormEvent, labelId: string) {
    e.preventDefault();
    if (!editName.trim()) return;
    setSaving(true);
    setError('');
    try {
      const updated = await labelsApi.update(
        workspaceId,
        labelId,
        { name: editName.trim(), color: editColor },
        getToken,
        refresh,
      );
      setLabels(prev => prev.map(l => (l.labelId === labelId ? updated : l)));
      setEditingId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update label');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(labelId: string) {
    setError('');
    try {
      await labelsApi.remove(workspaceId, labelId, getToken, refresh);
      setLabels(prev => prev.filter(l => l.labelId !== labelId));
      if (editingId === labelId) setEditingId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete label');
    }
  }

  return (
    <main className="page">
      <div className="toolbar" style={{ justifyContent: 'space-between' }}>
        <h1>Labels</h1>
        <div className="toolbar">
          <button type="button" onClick={handleCreateStarterSet} disabled={creatingStarterSet}>
            {creatingStarterSet ? 'Creating…' : 'Create starter set'}
          </button>
          {!showCreateForm && (
            <button type="button" onClick={() => setShowCreateForm(true)}>
              + Create label
            </button>
          )}
        </div>
      </div>

      {error && <p className="error">{error}</p>}

      {showCreateForm && (
        <form onSubmit={handleCreate} className="form-card" style={{ flexDirection: 'row', alignItems: 'center' }}>
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Label name" required autoFocus />
          <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)} />
          <div className="form-actions" style={{ marginTop: 0 }}>
            <button type="submit" disabled={creating}>
              {creating ? 'Creating…' : 'Create label'}
            </button>
            <button type="button" onClick={() => setShowCreateForm(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading && <p className="muted">Loading…</p>}
      {!loading && labels.length === 0 && <p className="muted">No labels yet.</p>}

      <ul className="card-list" style={{ marginTop: 16 }}>
        {labels.map(label => (
          <li
            key={label.labelId}
            className="card"
            style={{ borderLeft: `4px solid ${label.color}` }}
          >
            {editingId === label.labelId ? (
              <form onSubmit={e => handleSave(e, label.labelId)} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input value={editName} onChange={e => setEditName(e.target.value)} required />
                <input type="color" value={editColor} onChange={e => setEditColor(e.target.value)} />
                <button type="submit" disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" onClick={() => setEditingId(null)}>
                  Cancel
                </button>
              </form>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      background: label.color,
                      borderRadius: 2,
                      marginRight: 8,
                      verticalAlign: 'middle',
                    }}
                  />
                  {label.name}
                </span>
                <div className="toolbar">
                  <button type="button" className="icon-btn" title="Edit label" onClick={() => startEdit(label)}>
                    <PencilIcon />
                  </button>
                  <DeleteButton onConfirm={() => handleDelete(label.labelId)} title="Delete label" />
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </main>
  );
}
